import React, { FC, useState, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Container, Typography, Box } from '@material-ui/core';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link, Redirect } from 'react-router-dom';

import CircleCheckIcon from '../../assets/icons/CircleCheckIcon';
import { IntroBlock } from '../../components/IntroBlock';
import { Loader } from '../../components/Loader';
import { SeoBlock } from '../../components/SeoBlock';
import { Switch } from '../../components/Switch';
import { CustomButton } from '../../components/UI/CustomButton';
import { GET_PRICES_LIST } from '../../graphql/prices';
import { MY_MEMBERSHIP_ROUTE } from '../../routes';
import { GetPricesListArgs, LearnerInfo, Query, Tenant } from '../../types';
import { translate, getSymbolFromCurrency, getPriceVal } from '../../utils/helpers';
import { useAuth } from '../../utils/hooks';
import useStyles from './styles';

const PRICE_PAGE_SEO_IMG = '/headers-images/bg-intro.jpg';

const INTRO_DATA = {
  heading: translate('page.price.heading'),
  description: translate('page.price.description'),
};

const MONTHLY_DURATION_ID = 1;
const ANNUAL_DURATION_ID = 2;

interface PricePageProps {
  data?: any;
  isBlock?: boolean;
  userInfo?: LearnerInfo;
  userLoading?: boolean;
  tenant?: Tenant | null;
}

const PricePage: FC<PricePageProps> = ({
  data = { background: 'Dark' },
  isBlock = false,
  userInfo,
  userLoading,
  tenant,
}) => {
  const background = data?.background;
  const { t } = useTranslation();
  const classes = useStyles(isBlock, background)();
  const [priceTypeId, setPriceTypeId] = useState(ANNUAL_DURATION_ID);
  const [value, setValue] = useState(true);

  const { loginWithRedirect, isInitialized, isLoading } = useAuth();

  const toggleSwitch = (customValue: null | boolean) => {
    if (customValue == null) {
      setValue(!value);
      switchHandler(!value);
    } else {
      setValue(customValue);
      switchHandler(customValue);
    }
  };

  const switchHandler = (value) => {
    if (value) {
      setPriceTypeId(ANNUAL_DURATION_ID);
    } else {
      setPriceTypeId(MONTHLY_DURATION_ID);
    }
  };

  const handleSelectPlan = (plan) => {
    loginWithRedirect({
      redirectUri: `/pricing/${plan?.chargebee_plan_id}`,
      mode: 'signUp',
    });
  };

  const { data: pricesData, loading } = useQuery<Pick<Query, 'listPricings'>, GetPricesListArgs>(
    GET_PRICES_LIST,
    {
      variables: { pricingDurationId: priceTypeId },
    },
  );

  const prices = useMemo(() => pricesData?.listPricings || [], [pricesData]);
  const pivotIndex = pricesData ? Math.floor(pricesData.listPricings.length / 2) : -1;
  const maxUsers = useMemo(() => {
    let users = 0;
    prices.forEach((item) => {
      if (
        item?.chargebee_plan_info?.max_team_members &&
        item.chargebee_plan_info.max_team_members > users
      ) {
        users = item?.chargebee_plan_info?.max_team_members;
      }
    });

    return users;
  }, [prices]);

  const seoData = useMemo(
    () => ({
      seo_title: tenant?.title
        ? `${t('page.price.heading')} - ${tenant?.title}`
        : t('page.price.heading'),
      seo_description: t('page.price.description'),
      share_image: PRICE_PAGE_SEO_IMG,
    }),
    [tenant, t],
  );

  if (!isInitialized || isLoading || userLoading) {
    return (
      <>
        <SeoBlock data={seoData} />
        <Loader />
      </>
    );
  }

  if (userInfo?.has_subscription || userInfo?.owned_subscription) {
    return <Redirect to={MY_MEMBERSHIP_ROUTE} />;
  }

  return (
    <>
      <SeoBlock data={seoData} />
      <Box className={cx(classes.mainWrapper, isBlock && 'price-block')}>
        <Container className={classes.container} maxWidth={false}>
          {!isBlock && (
            <IntroBlock
              blockClass={classes.blockClass}
              headerClass={classes.headerClass}
              descriptionClass={classes.descriptionClass}
              className={classes.intro}
              data={INTRO_DATA}
              video={false}
              hasGradient={true}
              isSmall={false}
              tenant={tenant}
            />
          )}

          <Container className={classes.wrapper}>
            <Box className={classes.switchWrapper}>
              <span onClick={() => toggleSwitch(false)}>{t('common.monthly')}</span>

              <Switch value={value} onChange={() => toggleSwitch(null)} data-testid="planDurationSwitcher"/>

              <span onClick={() => toggleSwitch(true)}>
                {t('common.annual')} (<span>{t('common.save20')}</span>)
              </span>
            </Box>

            {/*Cards wrapper*/}
            {loading ? (
              <div className={classes.loaderWrapper}>
                <Loader global={false} />
              </div>
            ) : (
              <Box className={classes.cardsWrapper}>
                {/*Card component*/}
                {prices &&
                  prices.map((item, index) => {
                    const id = item?.id;
                    const title = item?.title;
                    const isIndividual =
                      !item.enterprise &&
                      ((!item?.chargebee_plan_info?.max_team_members ??
                        !item?.chargebee_plan_info?.min_team_members) ||
                        (item?.chargebee_plan_info?.max_team_members === 1 &&
                          item?.chargebee_plan_info?.min_team_members === 1));
                    const priceVal = getPriceVal(
                      isIndividual && item?.chargebee_plan_info?.is_taxable
                        ? item?.chargebee_plan_info?.price_with_tax
                        : item?.chargebee_plan_info?.price,
                    );
                    const price = priceVal.length ? priceVal[0].split('.') : [];
                    const priceCurrency = priceVal.length > 1 ? priceVal[1] : 'GBP';
                    const priceCurrencySymbol = getSymbolFromCurrency(priceCurrency);
                    const description = item?.description;
                    const features = item?.features;
                    const featureHeading = item?.features_heading;
                    const isPivot = pivotIndex === index;
                    const trial = item?.free_trial_period;

                    return (
                      <Box className={cx(classes.card, isPivot && classes.pivotCard)} key={id} data-testid={`pricingCard${id}`}>
                        <Box className={classes.cardHeader}>
                          <Typography
                            variant="h2"
                            component="h2"
                            align="center"
                            className={classes.title}
                          >
                            {title}
                          </Typography>

                          {/*Subtitle*/}
                          <div className={classes.subTitle}>
                            {item?.enterprise
                              ? t('page.account.enterpriseLearners', { amount: maxUsers + 1 })
                              : isIndividual
                              ? t('page.account.singleLearner')
                              : t('page.account.teamLearners', {
                                  min: item?.chargebee_plan_info?.min_team_members,
                                  max: item?.chargebee_plan_info?.max_team_members,
                                })}
                          </div>
                        </Box>

                        {/*Inner card*/}
                        <Box className={classes.innerCard}>
                          {item.enterprise ? (
                            <div className={classes.enterpriseHeader}>
                              {t('page.price.enterpriseHeader')}
                            </div>
                          ) : (
                            <div className={classes.priceBlock}>
                              <div>{priceCurrencySymbol}</div>
                              <div>
                                {price.map((number) => (
                                  <span className={classes.price}>{number}</span>
                                ))}
                              </div>
                              <div>
                                /
                                {priceTypeId === MONTHLY_DURATION_ID
                                  ? t('page.account.perMonth')
                                  : t('page.account.perYear')}
                              </div>
                            </div>
                          )}

                          {description && <div className={classes.description}>{description}</div>}
                          {featureHeading && (
                            <Typography className={classes.featureHeading}>
                              {featureHeading}
                            </Typography>
                          )}

                          <Box>
                            {features?.map(
                              (feature) =>
                                feature && (
                                  <div key={feature} className={classes.feature}>
                                    <CircleCheckIcon className={classes.featureIcon} />
                                    {feature}
                                  </div>
                                ),
                            )}
                            {Boolean(trial && trial.count && trial.unit) && (
                              <div className={classes.feature}>
                                <CircleCheckIcon className={classes.featureIcon} />
                                {t(`common.${trial?.unit}`, { count: trial?.count })}{' '}
                                {t('page.price.freeTrial')}
                              </div>
                            )}
                          </Box>
                          {item.enterprise ? (
                            <Link to={'/contact'}>
                              <CustomButton btnClass={classes.btn}>
                                {t('button.contactSales')}
                              </CustomButton>
                            </Link>
                          ) : userInfo ? (
                            <Link to={`/pricing/${item?.chargebee_plan_id}`}>
                              <CustomButton btnClass={classes.btn}>
                                {!!trial && !!trial?.count
                                  ? t('button.startTrial')
                                  : t('button.subscribe')}
                              </CustomButton>
                            </Link>
                          ) : (
                            <CustomButton
                              btnClass={classes.btn}
                              onClick={() => handleSelectPlan(item)}
                            >
                              {!!trial && !!trial?.count
                                ? t('button.startTrial')
                                : t('button.joinNow')}
                            </CustomButton>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            )}
          </Container>
        </Container>
      </Box>
    </>
  );
};

export default PricePage;
